import React, { useState, useMemo, useEffect } from 'react';
import {
    Calendar,
    ArrowUpCircle,
    ArrowDownCircle,
    Wallet,
    CreditCard, // Keep for potential future use or remove if unused warning
    Clock,
    TrendingUp,
    TrendingDown,
    X,
    Save,
    History
} from 'lucide-react';
import SummaryApi from '../../common'; // Import SummaryApi
import { toast } from 'react-toastify';
import fetchWithAuth from '../../common/fetchWithAuth';

const RojmiManagement = () => {
    // --- State ---
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedBank, setSelectedBank] = useState(''); // New State for Bank Filter
    const [transactionList, setTransactionList] = useState([]); // Single source of truth from API
    const [openingBalance, setOpeningBalance] = useState(0); // State for Opening Balance from API
    const [loading, setLoading] = useState(false); // Add loading state
    const [bankList, setBankList] = useState([]); // List of banks for dropdown

    // --- Cash Counter State ---
    const [showCashModal, setShowCashModal] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false); // New state for modal mode
    const [cashCounts, setCashCounts] = useState({}); // { 2000: 5, 500: 10 ... }
    const [totalCashCount, setTotalCashCount] = useState(0); // Store the saved total
    const [prevDayCashCount, setPrevDayCashCount] = useState(0); // Store previous day's tally

    // --- Fetch Bank List ---
    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const response = await fetchWithAuth(SummaryApi.getAllBanks.url, {
                    method: SummaryApi.getAllBanks.method,
                });
                const data = await response.json();
                if (data.success) {
                    const banks = data.data || [];
                    setBankList(banks);
                    // Set 'Cash' as default if available
                    const cashAcc = banks.find(b => b.accountName && b.accountName.toLowerCase().includes('cash'));
                    if (cashAcc) {
                        setSelectedBank(cashAcc._id);
                    }
                }
            } catch (error) {
                console.error("Error fetching banks:", error);
            }
        };
        fetchBanks();
    }, []);

    // ... (rest of the file until button)

    {/* --- Cash Counter Button (Visible only for Cash Account) --- */ }
    {
        selectedBank && bankList.find(b => b._id === selectedBank)?.accountName.toLowerCase().includes('cash') && (
            <button
                onClick={() => {
                    setIsReadOnly(false);
                    setShowCashModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg shadow-sm hover:bg-emerald-700 transition"
            >
                <Wallet size={18} />
                Count Cash
            </button>
        )
    }

    // --- Fetch Data Effect ---
    useEffect(() => {
        const fetchDayBookData = async () => {
            setLoading(true);
            try {
                // If "All Accounts" is disabled, we must require a bank Selection.
                if (!selectedBank) {
                    setLoading(false);
                    return;
                }

                // Call the API endpoint defined in SummaryApi (getDayBookTransactions)
                // Append bankId query parameter if selected
                let url = `${SummaryApi.getDayBookTransactions.url(selectedDate)}`;
                url += `&bankId=${selectedBank}`;
                const response = await fetchWithAuth(url, {
                    method: SummaryApi.getDayBookTransactions.method,
                });

                const data = await response.json();

                if (data.success) {
                    setTransactionList(data.data.transactions || []);
                    setOpeningBalance(data.data.openingBalance || 0);
                    setPrevDayCashCount(data.data.prevDayCashCount || 0);
                    // NEW: Load saved cash tally if exists
                    if (data.data.cashTally) {
                        const { denominations, totalAmount } = data.data.cashTally;
                        // Map array back to object { 2000: count, ... }
                        const countsObj = {};
                        if (Array.isArray(denominations)) {
                            denominations.forEach(d => {
                                countsObj[d.note] = d.count;
                            });
                        }
                        setCashCounts(countsObj);
                        setTotalCashCount(totalAmount);
                    } else {
                        // Reset if no data for this day
                        setCashCounts({});
                        setTotalCashCount(0);
                    }

                } else {
                    console.error("Failed to fetch DayBook data:", data.message);
                    setTransactionList([]);
                    setOpeningBalance(0);
                }
            } catch (error) {
                console.error("Error fetching DayBook data:", error);
                setTransactionList([]);
                setOpeningBalance(0);
            } finally {
                setLoading(false);
            }
        };

        fetchDayBookData();

    }, [selectedDate, selectedBank]); // Re-run when date or bank changes


    // --- Derived State (Stats & Lists) ---
    const stats = useMemo(() => {
        // 1. Total Daily Sales (Cash/Bank Inflow)
        const inflowTransactions = transactionList.filter(t => t.type === 'Inflow');
        // Only sum up explicitly marked 'vikri' amounts
        const totalSalesInflow = inflowTransactions.reduce((acc, t) => acc + (Number(t.vikri) || 0), 0);

        // 2. Total Outflow (Real Expenses only)
        // Filter transactions where type is 'Outflow' AND category is NOT 'UdharGiven' (since UdharGiven doesn't reduce cash)
        const outflowTransactions = transactionList.filter(t => t.type === 'Outflow');

        // Real Expenses (PaidOut > 0)
        const totalOutflow = outflowTransactions.reduce((acc, t) => acc + (Number(t.paidOut) || 0), 0);

        // 3. Total Udhar Given (To Display in Card)
        // Sum 'udhar' from UdharGiven transactions
        const totalUdharGiven = outflowTransactions
            .filter(t => t.category === 'UdharGiven')
            .reduce((acc, t) => acc + (Number(t.udhar) || 0), 0);

        // 4. Opening Balance
        const currentOpeningBalance = Number(openingBalance);

        // 5. Cash in Hand (Dynamic Calculation) - REMOVED per user request
        // Now using Actual Bank Balance from bankList
        let selectedBankBalance = 0;
        if (selectedBank) {
            const bank = bankList.find(b => b._id === selectedBank);
            selectedBankBalance = bank ? bank.balance : 0;
        }

        return {
            totalSalesInflow,
            totalUdharAmount: totalUdharGiven,
            totalOutflow,
            cashInHand: selectedBankBalance, // OVERRIDE: Show Actual Bank Balance
            openingBalance: currentOpeningBalance,
            inflowTransactions,
            outflowTransactions
        };
    }, [transactionList, openingBalance, selectedBank, bankList]); // Add dependencies


    return (
        <div className="w-full pb-10">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <span className="text-4xl">📓</span> Rojmi (Daybook)
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Daily track of Sales & Expenses </p>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-4">
                    {/* Bank Filter */}
                    <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                        <CreditCard size={18} className="text-gray-500" />
                        <select
                            value={selectedBank}
                            onChange={(e) => setSelectedBank(e.target.value)}
                            className="outline-none text-sm font-medium text-gray-800 bg-transparent cursor-pointer border-none focus:ring-0"
                        >
                            <option value="" disabled>Select Account</option>
                            {bankList.map((bank) => (
                                <option key={bank._id} value={bank._id}>{bank.accountName}</option>
                            ))}
                        </select>
                    </div>

                    {/* Date Picker */}
                    <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                        <Calendar size={18} className="text-gray-500" />
                        <span className="text-sm font-semibold text-gray-600">Date:</span>
                        <input
                            // max={new Date().toISOString().split('T')[0]} // Disable future dates
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="outline-none text-sm font-medium text-gray-800 bg-transparent cursor-pointer"
                        />
                    </div>

                    {/* --- Cash Counter Button (Visible only for Cash Account) --- */}
                    {selectedBank && bankList.find(b => b._id === selectedBank)?.accountName.toLowerCase().includes('cash') && (
                        <button
                            onClick={() => {
                                setIsReadOnly(false);
                                setShowCashModal(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg shadow-sm hover:bg-emerald-700 transition"
                        >
                            <Wallet size={18} />
                            Count Cash
                        </button>
                    )}
                </div>
            </div>

            {/* --- CASH COUNTER MODAL --- */}
            {
                showCashModal && (
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
                        <div className="bg-white w-[500px] max-w-full rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
                            {/* Modal Header */}
                            <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-2xl">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">{isReadOnly ? "Cash Breakdown" : "Cash Calculator"}</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">{isReadOnly ? "View-only mode" : "Enter count for each denomination"}</p>
                                </div>
                                <button
                                    onClick={() => setShowCashModal(false)}
                                    className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Modal Body - Scrollable */}
                            <div className="p-5 overflow-y-auto">
                                <table className="w-full text-sm border-collapse border border-gray-200">
                                    <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
                                        <tr>
                                            <th className="border border-gray-300 px-4 py-2 text-center w-1/3">Note (₹)</th>
                                            <th className="border border-gray-300 px-4 py-2 text-center w-1/3">Count</th>
                                            <th className="border border-gray-300 px-4 py-2 text-right w-1/3">Amount (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[500, 200, 100, 50, 20, 10, 5, 2, 1].map((note) => (
                                            <tr key={note} className="hover:bg-gray-50 transition">
                                                <td className="border border-gray-300 px-4 py-2 text-center font-bold text-gray-700">
                                                    ₹ {note}
                                                </td>
                                                <td className="border border-gray-300 px-2 py-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        disabled={isReadOnly}
                                                        placeholder="0"
                                                        value={cashCounts[note] || ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                                                            setCashCounts(prev => ({ ...prev, [note]: val }));
                                                        }}
                                                        className={`w-full text-center outline-none bg-transparent font-medium rounded py-1 ${isReadOnly ? 'text-gray-500' : 'focus:bg-yellow-50'}`}
                                                    />
                                                </td>
                                                <td className="border border-gray-300 px-4 py-2 text-right font-mono text-gray-800">
                                                    {((cashCounts[note] || 0) * note).toLocaleString('INR')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-bold text-gray-900 border-t-2 border-gray-300">
                                        <tr>
                                            <td colSpan="2" className="border border-gray-300 px-4 py-3 text-right uppercase text-xs tracking-wider">
                                                Total Cash
                                            </td>
                                            <td className="border border-gray-300 px-4 py-3 text-right text-emerald-700 text-lg">
                                                ₹ {Object.keys(cashCounts).reduce((acc, note) => acc + (note * (cashCounts[note] || 0)), 0).toLocaleString('INR')}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Modal Footer */}
                            {!isReadOnly && <div className="p-5 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50 rounded-b-2xl">
                                <button
                                    onClick={() => setShowCashModal(false)}
                                    className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={async () => {
                                        const notes = [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1];
                                        const total = notes.reduce((acc, note) => acc + (note * (cashCounts[note] || 0)), 0);

                                        // Prepare Array for Backend
                                        const denominationsArray = notes.map(note => ({
                                            note,
                                            count: cashCounts[note] || 0,
                                            amount: note * (cashCounts[note] || 0)
                                        })).filter(d => d.count > 0); // Only save non-zero

                                        try {
                                            const response = await fetchWithAuth(SummaryApi.saveCashTally.url, {
                                                method: SummaryApi.saveCashTally.method,
                                                body: JSON.stringify({
                                                    date: selectedDate,
                                                    bankId: selectedBank,
                                                    denominations: denominationsArray,
                                                    totalAmount: total
                                                })
                                            });
                                            const resData = await response.json();
                                            if (resData.success) {
                                                setTotalCashCount(total);
                                                toast.success(`Cash Count Saved: ₹${total.toLocaleString()}`);
                                                setShowCashModal(false);
                                            } else {
                                                toast.error(resData.message || 'Failed to save');
                                            }
                                        } catch (error) {
                                            console.error("Save Cash Tally Error:", error);
                                            toast.error('Error saving cash count');
                                        }
                                    }}
                                    className="px-6 py-2.5 bg-emerald-600 text-white font-bold rounded-xl shadow-lg hover:bg-emerald-700 transition flex items-center gap-2"
                                >
                                    <Save size={18} />
                                    Save Count
                                </button>
                            </div>}
                        </div>
                    </div>
                )
            }

            {/* --- KPI CARDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">

                {/* 1. Cash In Hand (Main Metric) */}
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white shadow-lg relative overflow-hidden group">
                    <div className="absolute right-0 top-0 opacity-10 transform translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform">
                        <Wallet size={80} />
                    </div>
                    <div className="relative z-10">
                        <p className="text-blue-100 text-sm font-medium uppercase tracking-wider mb-1">System Cash (Bill Amount)</p>
                        {/* <h2 className="text-3xl font-bold">{loading ? "..." : stats.inflowTransactions.length == 0 && stats.outflowTransactions.length == 0 ? "₹ 0" : `₹ ${stats.openingBalance.toLocaleString('INR')}`}</h2> */}
                        <h2 className="text-3xl font-bold">{loading ? "..." : `₹ ${stats.cashInHand.toLocaleString('INR')}`}</h2>
                    </div>
                </div>

                {/* 2. Total Sales Inflow (Vikri) */}
                <div className="bg-white rounded-2xl p-5 border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Today's Inflow (VIKRI)</p>
                            <h2 className="text-2xl font-bold text-emerald-600 mt-1">{loading ? "..." : `₹ ${stats.totalSalesInflow.toLocaleString('INR')}`}</h2>
                        </div>
                        <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                            <ArrowDownCircle size={24} />
                        </div>
                    </div>
                </div>

                {/* 3. Total Outflow (Expenses) */}
                <div className="bg-white rounded-2xl p-5 border border-rose-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Today's Outflow (KHARCH)</p>
                            <h2 className="text-2xl font-bold text-rose-600 mt-1">{loading ? "..." : `₹ ${stats.totalOutflow.toLocaleString('INR')}`}</h2>
                        </div>
                        <div className="p-2 bg-rose-50 text-rose-600 rounded-lg">
                            <ArrowUpCircle size={24} />
                        </div>
                    </div>
                </div>

                {/* Opening Balance Card (New/Replaced) */}
                <div className="bg-white rounded-2xl p-5 border border-yellow-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">CREDIT GIVEN (UDHAR)</p>
                            <h2 className="text-2xl font-bold text-yellow-600 mt-1">₹ {stats.totalUdharAmount.toLocaleString('INR')}</h2>
                        </div>
                        <div className="p-2 bg-yellow-50 text-yellow-600 rounded-lg">
                            <Clock size={24} />
                        </div>
                    </div>
                </div>

                {/* --- NEW CARDS: Cash Reconciliation (Visible only for Cash Account) --- */}
                {selectedBank && bankList.find(b => b._id === selectedBank)?.accountName.toLowerCase().includes('cash') && (
                    <>
                        {/* 1. System Cash (Bill Amount)
                        <div className="bg-white rounded-2xl p-5 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">System Cash (Bill Amount)</p>
                                    <h2 className="text-2xl font-bold text-blue-600 mt-1">₹ {(15000).toLocaleString('INR')}</h2>
                                </div>
                                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                    <CreditCard size={24} />
                                </div>
                            </div>
                        </div> */}

                        {/* 2. Physical Cash (Cash Count) */}
                        <div
                            onClick={() => {
                                setIsReadOnly(true);
                                setShowCashModal(true);
                            }}
                            className="bg-white rounded-2xl p-5 border border-emerald-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">TOTAL Cash (Count)</p>
                                    <h2 className={`text-2xl font-bold mt-1 text-emerald-600`}>
                                        ₹ {totalCashCount.toLocaleString('INR')}
                                    </h2>
                                </div>
                                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                                    <Wallet size={24} />
                                </div>
                            </div>
                        </div>

                        {/* 3. Previous Day Cash */}
                        <div className="bg-white rounded-2xl p-5 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Previous Day Cash</p>
                                    <h2 className="text-2xl font-bold text-purple-600 mt-1">₹ {prevDayCashCount.toLocaleString('INR')}</h2>
                                </div>
                                <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                                    <History size={24} />
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* --- MAIN CONTENT GRID --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* LEFT: INFLOW LIST */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-xl">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-emerald-100 text-emerald-600 rounded-lg">
                                <TrendingUp size={18} />
                            </div>
                            <h2 className="text-lg font-bold text-gray-800">Inflow <span className="text-gray-400 text-sm font-normal">(Money In)</span></h2>
                        </div>
                    </div>

                    <div className="p-0 overflow-x-auto flex-grow">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold">
                                <tr>
                                    <th className="px-4 py-3">PARTICULARS</th>
                                    <th className="px-4 py-3 text-center">TOTAL</th>
                                    <th className="px-4 py-3 text-center">PAID (IN)</th>
                                    <th className="px-4 py-3 text-center">UDHAR</th>
                                    <th className="px-4 py-3 text-center">CATEGORY</th>
                                    <th className="px-4 py-3 text-center">MODE</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-4 py-8 text-center">
                                            <div className="flex justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                                            </div>
                                            <p className="text-gray-400 mt-2 text-sm">Loading inflow...</p>
                                        </td>
                                    </tr>
                                ) : stats.inflowTransactions.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-8 text-center text-gray-400 text-sm">
                                            No inflow recorded today.
                                        </td>
                                    </tr>
                                ) : (
                                    stats.inflowTransactions.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors text-sm">
                                            <td className="px-4 py-3">
                                                <div className="font-semibold text-gray-800">{item.name}</div>
                                                <div className="text-xs text-gray-400">{item.description}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center font-mono text-gray-500">
                                                {item?.totalAmount.toLocaleString('INR')}
                                            </td>
                                            <td className="px-4 py-3 text-center font-mono font-bold text-emerald-600">
                                                ₹{item.paidIn.toLocaleString('INR')}
                                            </td>
                                            <td className="px-4 py-3 text-center font-mono text-amber-600">
                                                {item.udhar > 0 ? `₹${item.udhar.toLocaleString('INR')}` : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`px-2 py-0.5 rounded text-xs border ${item.category === 'FullPayment' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                                    item.category === 'PartialPayment' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                                        item.category === 'FullUdhar' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                                            item.category === 'OldDebt' ? 'bg-purple-50 text-purple-700 border-purple-100' :
                                                                'bg-gray-50 text-gray-600 border-gray-200'
                                                    }`}>
                                                    {item.category || '-'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs border border-gray-200">
                                                    {item.mode}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* RIGHT: OUTFLOW LIST */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-xl">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-rose-100 text-rose-600 rounded-lg">
                                <TrendingDown size={18} />
                            </div>
                            <h2 className="text-lg font-bold text-gray-800">Outflow <span className="text-gray-400 text-sm font-normal">(Money Out)</span></h2>
                        </div>
                    </div>

                    <div className="p-0 overflow-x-auto flex-grow">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold">
                                <tr>
                                    <th className="px-4 py-3">Details</th>
                                    <th className="px-4 py-3 text-right">Amount</th>
                                    <th className="px-4 py-3 text-center">Category</th>
                                    <th className="px-4 py-3 text-center">Mode</th>
                                    <th scope="col" className="px-4 py-3 text-center">STATUS</th>
                                    <th className="px-4 py-3 text-center">Note</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-8 text-center">
                                            <div className="flex justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
                                            </div>
                                            <p className="text-gray-400 mt-2 text-sm">Loading outflow...</p>
                                        </td>
                                    </tr>
                                ) : stats.outflowTransactions.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" className="pl-28 py-8 text-center text-gray-400 text-sm">
                                            No outflow recorded today.
                                        </td>
                                    </tr>
                                ) : (
                                    stats.outflowTransactions.map((item, index) => (
                                        <tr key={index} className={`hover:bg-gray-50 transition-colors text-sm ${item.status === 'Cancelled' ? 'opacity-50 bg-gray-100' : ''}`}>
                                            <td className={`px-3 py-2 ${item.status === 'Cancelled' ? 'line-through decoration-gray-400' : ''}`}>
                                                <div className="font-semibold text-gray-800">{item.name}</div>
                                                {/* <div className="text-xs text-gray-400">{item.name}</div> */}
                                            </td>
                                            <td className={`px-4 py-3 text-right font-mono font-bold text-rose-600 ${item.status === 'Cancelled' ? 'line-through decoration-gray-400' : ''}`}>
                                                ₹{(item.category === 'UdharGiven' ? item.udhar : item.paidOut).toLocaleString('INR')}
                                            </td>
                                            <td className={`px-4 py-3 text-center ${item.status === 'Cancelled' ? 'line-through decoration-gray-400' : ''}`}>
                                                <span className={`px-2 py-0.5 rounded text-xs border ${item.category === 'UdharGiven' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                                    'bg-gray-50 text-gray-600 border-gray-200'
                                                    }`}>
                                                    {item.category || '-'}
                                                </span>
                                            </td>
                                            <td className={`px-4 py-3 text-center ${item.status === 'Cancelled' ? 'line-through decoration-gray-400' : ''}`}>
                                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs border border-gray-200">
                                                    {item.mode}
                                                </span>
                                            </td>
                                            <td className={`py-3 px-4 ${item.status === 'Cancelled' ? 'line-through decoration-gray-400' : ''}`}>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                                                     ${item.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                                        item.status === 'Pending' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                                            item.status === 'Cancelled' ? 'bg-gray-100 text-gray-500  border-gray-200' :
                                                                item.status === 'Failed' ? 'bg-red-50 text-red-700 border-red-100' :
                                                                    'bg-blue-50 text-blue-700 border-blue-100' // Bounced or Default
                                                    }`}>
                                                    {item.status || 'Completed'}
                                                </span>
                                            </td>
                                            <td className={`px-4 py-3 text-center ${item.status === 'Cancelled' ? 'line-through decoration-gray-400' : ''}`}>
                                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs border border-gray-200">
                                                    {item.description}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default RojmiManagement;